import type { IPeerConnection } from './types';
import { IceHealthGuard } from './IceHealthGuard';
import type { GuestPeerLogger } from './guestPeerSessionTypes';
import type { GuestCongestionMonitor } from './guestCongestionMonitor';
import {
  BEACON_WEBRTC_E2E_HOOKS_ENV,
  PLAYCAST_BEACON_E2E_WINDOW_KEY,
  isBeaconE2eHooksEnabled,
  isBeaconE2eCongestionLevel,
  type BeaconE2eCongestionLevel,
  type BeaconE2eSnapshot,
  type PlaycastBeaconE2eApi,
} from './beaconWebrtcE2eContracts';

export type GuestBeaconE2eHost = {
  getBeaconE2eSnapshot: () => BeaconE2eSnapshot;
  injectCongestion: (level: BeaconE2eCongestionLevel) => void;
};

/**
 * Attach window.__playcastBeaconE2e when BEACON_WEBRTC_E2E_HOOKS is enabled.
 * Returns the installed api as an ownership handle for detach, or null when the
 * hooks are disabled or unavailable.
 */
export function attachGuestBeaconE2eHooks(
  host: GuestBeaconE2eHost,
): PlaycastBeaconE2eApi | null {
  if (typeof window === 'undefined') return null;
  const env =
    typeof process !== 'undefined' && process.env
      ? process.env[BEACON_WEBRTC_E2E_HOOKS_ENV]
      : undefined;
  if (!isBeaconE2eHooksEnabled(env)) return null;

  const api: PlaycastBeaconE2eApi = {
    getSnapshot: () => host.getBeaconE2eSnapshot(),
    injectCongestion: (level) => host.injectCongestion(level),
  };
  (window as unknown as Record<string, unknown>)[PLAYCAST_BEACON_E2E_WINDOW_KEY] = api;
  return api;
}

/**
 * Remove the global hook only if `owned` is the api currently installed, so a
 * late dispose can't rip out a newer session's hooks.
 */
export function detachGuestBeaconE2eHooks(owned: PlaycastBeaconE2eApi | null): void {
  if (typeof window === 'undefined') return;
  if (!owned) return;
  const globals = window as unknown as Record<string, unknown>;
  if (globals[PLAYCAST_BEACON_E2E_WINDOW_KEY] !== owned) return;
  delete globals[PLAYCAST_BEACON_E2E_WINDOW_KEY];
}

export function buildGuestBeaconSnapshot(congestion: GuestCongestionMonitor): BeaconE2eSnapshot {
  // Pass sampled values straight through: 0ms RTT / 0% loss are real readings and
  // must stay distinguishable from "never sampled" (undefined). The accessors are now
  // backed by the shared sampler, which omits rather than zero-fills an unsampled value.
  return {
    role: 'client',
    rttMs: congestion.getLastRttMs(),
    packetsLostPct: congestion.getLastPacketsLostPct(),
    lastCongestionSent: congestion.getLastCongestionSent(),
  };
}

export function injectGuestCongestion(
  connection: IPeerConnection | null,
  congestion: GuestCongestionMonitor,
  level: BeaconE2eCongestionLevel,
): void {
  if (!isBeaconE2eCongestionLevel(level)) return;
  if (!connection) return;
  congestion.injectCongestion(connection, level);
}

export function logRemoteCandidateCounts(
  connection: IPeerConnection,
  iceHealth: typeof IceHealthGuard,
  logger?: GuestPeerLogger,
): void {
  iceHealth.getCandidateCounts(connection).then((counts) => {
    if (!counts) return;
    logger?.info?.(
      `Post-SDP stats: ${counts.remoteCandidates} remote candidates, ${counts.candidatePairs} candidate pairs`,
    );
    logger?.breadcrumb?.(
      'Post-setRemoteDescription candidate stats',
      counts.remoteCandidates === 0 ? 'warning' : 'info',
      counts as unknown as Record<string, unknown>,
    );
    if (counts.remoteCandidates === 0) {
      logger?.warn?.('No remote candidates after setRemoteDescription — ICE will fail');
    }
  });
}

export function recordGuestSdpBreadcrumb(
  sdp: RTCSessionDescriptionInit,
  sdpInspector: { getMediaKinds: (sdp: string | undefined) => string[] },
  hostPeerId: string | undefined,
  logger?: GuestPeerLogger,
): void {
  const kinds = sdpInspector.getMediaKinds(sdp.sdp);
  const hasVideo = kinds.includes('video');
  logger?.breadcrumb?.(
    `Viewer local SDP (${sdp.type}) m-sections`,
    hasVideo ? 'info' : 'warning',
    { type: sdp.type, kinds, hostPeerId },
  );
  if (!hasVideo) {
    logger?.warn?.(
      `Local SDP (${sdp.type}) has no m=video — expected recvonly video transceiver.`,
      { kinds },
    );
  }
}

export function clearTimer(timer: ReturnType<typeof setTimeout> | null): null {
  if (timer) clearTimeout(timer);
  return null;
}
